import { Link, useRouterState } from "@tanstack/react-router";
import { Home, Compass, MessageCircle, BarChart3, User } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import { springs } from "@andrum/ui";
import { hapticService } from "@/lib/capacitor";
import { cn } from "@/lib/utils";

const tabDefs = [
  { to: "/app/today", labelKey: "nav.today", icon: Home },
  { to: "/app/explore", labelKey: "nav.explore", icon: Compass },
  { to: "/app/coach", labelKey: "nav.coach", icon: MessageCircle },
  { to: "/app/insights", labelKey: "nav.insights", icon: BarChart3 },
  { to: "/app/profile", labelKey: "nav.profile", icon: User },
] as const;

export function BottomNavigation() {
  const { t } = useTranslation();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const reduce = useReducedMotion();

  return (
    <nav
      className="bottom-nav-shell fixed z-40"
      aria-label={t("nav.main")}
    >
      <ul className="flex h-full items-stretch justify-around gap-0.5 px-1.5 py-1">
        {tabDefs.map((tab) => {
          const isActive = currentPath.startsWith(tab.to);
          const Icon = tab.icon;
          return (
            <li key={tab.to} className="flex min-w-0 flex-1">
              <Link
                to={tab.to}
                preload="intent"
                onClick={() => {
                  void hapticService.selection();
                }}
                className={cn(
                  "group relative flex h-full w-full cursor-pointer flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1 text-[10.5px] transition-colors",
                  isActive
                    ? "text-primary"
                    : "text-foreground-muted hover:text-foreground"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span
                  className={cn(
                    "absolute inset-x-0.5 inset-y-0.5 rounded-full transition-colors",
                    isActive
                      ? "bg-primary-soft"
                      : "bg-transparent group-hover:bg-surface-muted/80 group-focus-visible:bg-surface-muted/80"
                  )}
                  aria-hidden
                />
                {isActive ? (
                  <motion.span
                    layoutId="nav-indicator"
                    className="absolute inset-x-0.5 inset-y-0.5 rounded-full bg-primary-soft"
                    transition={reduce ? { duration: 0 } : springs.soft}
                    aria-hidden
                  />
                ) : null}
                <motion.span
                  className="relative z-10"
                  animate={
                    isActive && !reduce
                      ? { scale: 1.06, y: -0.5 }
                      : { scale: 1, y: 0 }
                  }
                  transition={springs.responsive}
                >
                  <Icon
                    size={21}
                    strokeWidth={isActive ? 2.2 : 1.7}
                    aria-hidden
                  />
                </motion.span>
                <span className="relative z-10 max-w-full truncate px-0.5 text-center font-medium leading-tight">
                  {t(tab.labelKey)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
